import React, { useState, useEffect } from 'react';
import { Clock, Users, Calendar, BarChart3, LogIn, LogOut, User, Settings, FileText, Download, CheckSquare, MessageSquare, Filter, Bell, X, Folder, AlertCircle, Briefcase, UserCog, TrendingUp, PartyPopper, MapPin, Car, Target, ClipboardList, Menu, PieChart, Search, Home, MoreHorizontal, ChevronDown, RefreshCw, UserPlus } from 'lucide-react';
import PointeuseAvancee from './components/PointeuseAvancee';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Taches from './components/Taches';
import Planning from './components/Planning';
import Messagerie from './components/Messagerie';
import Employees from './components/Employees';
import ExportsPayroll from './components/ExportsPayroll';
import Conges from './components/Conges';
import ModulationWidget from './components/ModulationWidget';
import ModulationManagement from './components/ModulationManagement';
import Evenements from './components/Evenements';
import Notifications from './components/Notifications';
import Demandes from './components/Demandes';
import Vehicules from './components/Vehicules';
import Objectifs from './components/Objectifs';
import DashboardPatron from './components/DashboardPatron';
import DashboardEmployee from './components/DashboardEmployee';
import GestionProjetsKPI from './components/GestionProjetsKPI';
import ProjectWorkspace from './components/ProjectWorkspace';
import CongesSolde from './components/CongesSolde';
import ExportCongesHCR from './components/ExportCongesHCR';
import NoteRHConges from './components/NoteRHConges';
import DocumentsReglementaires from './components/DocumentsReglementaires';
import ThemeToggle from './components/ThemeToggle';
import RHSidebar, { getDefaultRHView } from './components/RHSidebar';
import CommandPalette from './components/CommandPalette';
import UserMenu from './components/UserMenu';
import CongesSoldeBanner from './components/CongesSoldeBanner';
import InscriptionRequests from './components/InscriptionRequests';
import { API_URL } from './config';

// Composant pour afficher l'heure actuelle (évite le re-render de tout le composant)
const CurrentTime = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return <>{time.toLocaleTimeString('fr-FR')}</>;
};

const StaffManagerWithAPI = () => {
  // États d'authentification
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetEmail, setResetEmail] = useState(null);

  const [currentView, setCurrentView] = useState('dashboard');
  // Sous-vue active dans l'onglet RH (employees, inscriptions, conges, solde_cp, note_rh, modulation, documents, demandes, export_paie, export_hcr)
  const [rhSubView, setRhSubView] = useState('conges');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // États pour les données de l'API
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [services, setServices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [timeRecords, setTimeRecords] = useState([]);
  const [pointages, setPointages] = useState([]);
  const [managerServices, setManagerServices] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [ptgSearch, setPtgSearch] = useState('');
  const [ptgExtrasFilter, setPtgExtrasFilter] = useState('sans'); // 'sans' | 'tous' | 'uniquement'

  // États pour les formulaires
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role_id: '',
    service_id: '',
    heures_hebdomadaires: 35,
    date_embauche: ''
  });

  // Vérifier l'authentification au chargement + détecter token de reset dans l'URL
  useEffect(() => {
    // Détecter les paramètres de réinitialisation dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlResetToken = urlParams.get('reset_token');
    const urlResetEmail = urlParams.get('email');

    if (urlResetToken && urlResetEmail) {
      setResetToken(urlResetToken);
      setResetEmail(urlResetEmail);
      // Nettoyer l'URL sans recharger la page
      window.history.replaceState({}, document.title, window.location.pathname);
      return; // Ne pas vérifier l'auth si on est en mode reset
    }

    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (token && user) {
      try {
        const parsedUser = JSON.parse(user);
        // Vérifier la validité du token auprès du backend
        fetch(`${API_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(response => {
          if (response.ok) {
            setAuthToken(token);
            setAuthUser(parsedUser);
            setIsAuthenticated(true);
            // Toujours démarrer sur le dashboard au rechargement de page
            setCurrentView('dashboard');
            setRhSubView(getDefaultRHView(parsedUser?.role_name));
          } else {
            // Token expiré ou invalide : déconnecter
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }).catch(() => {
          // Erreur réseau (serveur inaccessible) : conserver la session
          setAuthToken(token);
          setAuthUser(parsedUser);
          setIsAuthenticated(true);
          setCurrentView('dashboard');
          setRhSubView(getDefaultRHView(parsedUser?.role_name));
        });
      } catch (err) {
        // JSON invalide dans localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger les données au démarrage (seulement si authentifié)
  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated]);

  // Auto-sélectionner le profil pour les employés et extras (ils ne peuvent voir que leur propre pointeuse)
  useEffect(() => {
    if (authUser && ['Employé', 'Extra'].includes(authUser.role_name) && !currentUser) {
      setCurrentUser(authUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  // Si l'utilisateur arrive sur RH avec une sous-vue non accessible à son rôle, la repositionner
  useEffect(() => {
    if (!authUser || currentView !== 'rh') return;
    const role = authUser.role_name;
    // Restrictions par rôle sur les sous-vues RH
    const restrictedForExtra = ['employees', 'inscriptions', 'conges', 'solde_cp', 'note_rh', 'modulation', 'demandes', 'export_paie', 'export_hcr'];
    const restrictedForEmployee = ['employees', 'inscriptions', 'solde_cp', 'modulation', 'export_paie', 'export_hcr'];
    const restrictedForManager = ['inscriptions', 'export_paie', 'export_hcr'];
    const isRestricted = (
      (role === 'Extra' && restrictedForExtra.includes(rhSubView)) ||
      (role === 'Employé' && restrictedForEmployee.includes(rhSubView)) ||
      (role === 'Manager' && restrictedForManager.includes(rhSubView))
    );
    if (isRestricted) setRhSubView(getDefaultRHView(role));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, authUser, rhSubView]);

  // Raccourci clavier global pour Command Palette (Ctrl+K ou Cmd+K)
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (isAuthenticated) setShowCommandPalette(o => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isAuthenticated]);

  // ==================== FONCTIONS D'AUTHENTIFICATION ====================

  const handleLogin = (user, token) => {
    setAuthUser(user);
    setAuthToken(token);
    setIsAuthenticated(true);
    // Toujours démarrer sur le dashboard, quel que soit l'état précédent
    setCurrentView('dashboard');
    setRhSubView(getDefaultRHView(user?.role_name));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuthUser(null);
    setAuthToken(null);
    setIsAuthenticated(false);
    setCurrentUser(null);
    setSelectedEmployeeId(null);
    // Réinitialiser les données
    setEmployees([]);
    setRoles([]);
    setServices([]);
    setTasks([]);
    setSchedules([]);
    setTimeRecords([]);
    setPointages([]);
  };

  // ==================== FONCTIONS API ====================

  // Fonction générique pour les appels API
  const apiCall = async (endpoint, method = 'GET', data = null) => {
    try {
      const options = {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      };

      // Ajouter le token d'authentification si disponible
      if (authToken) {
        options.headers['Authorization'] = `Bearer ${authToken}`;
      }

      if (data) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(`${API_URL}${endpoint}`, options);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur API');
      }

      return await response.json();
    } catch (err) {
      console.error('Erreur API:', err);
      setError(err.message);
      throw err;
    }
  };

  // Charger toutes les données
  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [employeesData, rolesData, servicesData, tasksData, schedulesData, recordsData, pointagesData, managerServicesData, vehiculesData] = await Promise.all([
        apiCall('/employees'),
        apiCall('/roles'),
        apiCall('/services'),
        apiCall('/tasks'),
        apiCall('/schedules'),
        apiCall('/time-records'),
        apiCall('/pointages'),
        apiCall('/manager-services').catch(() => []),
        apiCall('/vehicules').catch(() => [])
      ]);

      setEmployees(employeesData);
      setRoles(rolesData);
      setServices(servicesData);
      setTasks(tasksData);
      setSchedules(schedulesData);
      setTimeRecords(recordsData);
      setPointages(pointagesData);
      setManagerServices(Array.isArray(managerServicesData) ? managerServicesData : []);
      setVehicules(Array.isArray(vehiculesData) ? vehiculesData : []);
      setLastUpdate(new Date());
    } catch (err) {
      setError('Impossible de charger les données. Assurez-vous que le serveur API est démarré (npm start).');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Ajouter un employé
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiCall('/employees', 'POST', employeeForm);
      await loadAllData();
      setShowAddEmployee(false);
      resetEmployeeForm();
      alert('Employé ajouté avec succès !');
    } catch (err) {
      alert('Erreur lors de l\'ajout de l\'employé: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Modifier un employé
  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiCall(`/employees/${editingEmployee}`, 'PUT', employeeForm);
      await loadAllData();
      setEditingEmployee(null);
      resetEmployeeForm();
      alert('Employé mis à jour avec succès !');
    } catch (err) {
      alert('Erreur lors de la mise à jour: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un employé
  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) return;

    setLoading(true);
    try {
      await apiCall(`/employees/${id}`, 'DELETE');
      await loadAllData();
      alert('Employé supprimé avec succès !');
    } catch (err) {
      alert('Erreur lors de la suppression: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pointage entrée
  const handleCheckIn = async (employeeId) => {
    setLoading(true);
    try {
      const now = new Date();
      const heureArrivee = now.toTimeString().slice(0, 5); // HH:MM
      const dateAujourdhui = now.toISOString().split('T')[0];

      const response = await fetch(`${API_URL}/pointages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          user_id: employeeId,
          date: dateAujourdhui,
          heure_arrivee: heureArrivee,
          statut: 'en_cours',
          notes: ''
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors du pointage');
      }

      await loadAllData();
      alert('Arrivée pointée avec succès !');
    } catch (err) {
      alert('Erreur lors du pointage: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pointage sortie
  const handleCheckOut = async (employeeId) => {
    setLoading(true);
    try {
      // Trouver le pointage en cours de l'employé aujourd'hui
      const today = new Date().toISOString().split('T')[0];
      const pointageEnCours = pointages.find(p =>
        p.user_id === employeeId &&
        p.date === today &&
        p.statut === 'en_cours'
      );

      if (!pointageEnCours) {
        alert('Aucun pointage en cours trouvé');
        return;
      }

      const now = new Date();
      const heureDepart = now.toTimeString().slice(0, 5); // HH:MM

      // Calculer les heures travaillées
      const [arrH, arrM] = pointageEnCours.heure_arrivee.split(':').map(Number);
      const [depH, depM] = heureDepart.split(':').map(Number);
      const arrMinutes = arrH * 60 + arrM;
      const depMinutes = depH * 60 + depM;
      let duree = (depMinutes - arrMinutes) / 60;

      // Déduction automatique de 45 min si > 4h et pas de pause
      if (duree > 4 && !pointageEnCours.pause_debut) {
        duree -= 0.75;
      }

      const heuresTravaillees = Math.max(0, duree).toFixed(2);

      const response = await fetch(`${API_URL}/pointages/${pointageEnCours.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          heure_depart: heureDepart,
          heures_travaillees: heuresTravaillees,
          statut: 'termine'
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors du pointage de sortie');
      }

      await loadAllData();
      alert('Départ pointé avec succès !');
    } catch (err) {
      alert('Erreur lors du pointage: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser le formulaire
  const resetEmployeeForm = () => {
    setEmployeeForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role_id: '',
      service_id: '',
      heures_hebdomadaires: 35,
      date_embauche: ''
    });
  };

  // Préparer l'édition
  const startEdit = (employee) => {
    setEditingEmployee(employee.id);
    setEmployeeForm({
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      phone: employee.phone || '',
      role_id: employee.role_id,
      service_id: employee.service_id,
      heures_hebdomadaires: employee.heures_hebdomadaires || 35,
      date_embauche: employee.date_embauche || ''
    });
    setShowAddEmployee(true);
  };

  // ==================== CALCULS ====================

  // Calculer les présences depuis les pointages d'aujourd'hui
  const today = new Date().toISOString().split('T')[0];
  const pointagesAujourdhui = pointages.filter(p => p.date === today && p.statut === 'en_cours');
  const employeesPresentsIds = new Set(pointagesAujourdhui.map(p => p.user_id));

  const stats = {
    totalEmployees: employees.length,
    present: employeesPresentsIds.size,
    absent: employees.length - employeesPresentsIds.size,
    tasksInProgress: tasks.filter(t => t.status === 'En cours').length,
    tasksTodo: tasks.filter(t => t.status === 'À faire').length,
    tasksCompleted: tasks.filter(t => t.status === 'Terminé').length
  };

  // ==================== COMPOSANTS DE VUE ====================

  // Dashboard spécifique pour les Extra
  const ExtraDashboardView = () => {
    const [nextEvent, setNextEvent] = useState(null);
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [myInvitations, setMyInvitations] = useState([]);
    const [loadingInvitations, setLoadingInvitations] = useState(true);
    const [pointageStatus, setPointageStatus] = useState(null);
    const [mySchedule, setMySchedule] = useState([]);

    useEffect(() => {
      if (!authUser || !authToken) return;

      // Fetch events
      const fetchEvents = async () => {
        try {
          const response = await fetch(`${API_URL}/evenements`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (response.ok) {
            const events = await response.json();
            const now = new Date();
            const futureEvents = events
              .filter(event => {
                const eventDate = new Date(event.date_debut);
                const isParticipant = event.participants &&
                  event.participants.some(p => p.employee_id === authUser.employee_id);
                return eventDate >= now && isParticipant;
              })
              .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));
            setNextEvent(futureEvents[0] || null);
            setUpcomingEvents(futureEvents.slice(0, 5));
          }
        } catch (error) {
          console.error('Erreur chargement événements:', error);
        } finally {
          setLoadingEvents(false);
        }
      };

      // Fetch invitations
      const fetchInvitations = async () => {
        try {
          const response = await fetch(`${API_URL}/extra/my-invitations`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (response.ok) {
            const data = await response.json();
            setMyInvitations(data);
          }
        } catch (error) {
          console.error('Erreur chargement invitations:', error);
        } finally {
          setLoadingInvitations(false);
        }
      };

      // Fetch pointage status
      const fetchPointage = async () => {
        try {
          const today = new Date().toISOString().split('T')[0];
          const response = await fetch(`${API_URL}/pointages?employee_id=${authUser.employee_id}&date=${today}`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (Array.isArray(data) && data.length > 0) {
              setPointageStatus(data[data.length - 1]);
            }
          }
        } catch (error) {
          console.error('Erreur chargement pointage:', error);
        }
      };

      // Fetch schedule for the week
      const fetchSchedule = async () => {
        try {
          const response = await fetch(`${API_URL}/schedules?employee_id=${authUser.employee_id}`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (response.ok) {
            const data = await response.json();
            const now = new Date();
            const weekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
            const upcoming = (Array.isArray(data) ? data : [])
              .filter(s => new Date(s.date) >= now && new Date(s.date) <= weekLater)
              .sort((a, b) => new Date(a.date) - new Date(b.date));
            setMySchedule(upcoming.slice(0, 7));
          }
        } catch (error) {
          console.error('Erreur chargement planning:', error);
        }
      };

      fetchEvents();
      fetchInvitations();
      fetchPointage();
      fetchSchedule();
    }, [authUser, authToken]);

    const respondToInvitation = async (invitationId, status) => {
      try {
        const response = await fetch(`${API_URL}/extra-invitations/${invitationId}/respond`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status })
        });
        if (response.ok) {
          setMyInvitations(prev =>
            prev.map(inv => inv.id === invitationId ? { ...inv, status, responded_at: new Date().toISOString() } : inv)
          );
        }
      } catch (error) {
        console.error('Erreur réponse invitation:', error);
      }
    };

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    };

    const formatTime = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    const pendingInvitations = myInvitations.filter(inv => inv.status === 'invited');

    return (
      <div className="space-y-6">
        {/* En-tête de bienvenue */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-8 text-white shadow-lg">
          <h1 className="text-3xl font-bold mb-2">Bienvenue, {authUser?.first_name} !</h1>
          <p className="text-orange-100">Espace Extra - Vos événements et invitations</p>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{upcomingEvents.length}</div>
            <div className="text-xs text-text-muted">Événements à venir</div>
          </div>
          <div className="bg-surface rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{pendingInvitations.length}</div>
            <div className="text-xs text-text-muted">Invitations en attente</div>
          </div>
          <div className="bg-surface rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {pointageStatus && pointageStatus.check_in && !pointageStatus.check_out ? 'Pointé' : 'Non pointé'}
            </div>
            <div className="text-xs text-text-muted">Pointeuse aujourd'hui</div>
          </div>
          <div className="bg-surface rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{mySchedule.length}</div>
            <div className="text-xs text-text-muted">Shifts cette semaine</div>
          </div>
        </div>

        {/* Pointeuse rapide */}
        <div className="bg-surface rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-text-primary mb-3 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-indigo-500" />
            Pointeuse
          </h2>
          {pointageStatus && pointageStatus.check_in ? (
            <div className="flex items-center justify-between bg-green-50 dark:bg-green-950/40 rounded-lg p-4">
              <div>
                <p className="text-green-800 dark:text-green-200 font-semibold">Pointé depuis {pointageStatus.check_in}</p>
                {pointageStatus.check_out && (
                  <p className="text-text-secondary text-sm">Dépointé à {pointageStatus.check_out}</p>
                )}
              </div>
              <button
                onClick={() => setCurrentView('pointeuse')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                Gérer
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-elevated-1 rounded-lg p-4">
              <p className="text-text-secondary">Vous n'êtes pas encore pointé aujourd'hui</p>
              <button
                onClick={() => setCurrentView('pointeuse')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                Pointer
              </button>
            </div>
          )}
        </div>

        {/* Invitations en attente */}
        {pendingInvitations.length > 0 && (
          <div className="bg-surface rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4">
              <h2 className="text-lg font-bold text-white">
                📧 Invitations en attente ({pendingInvitations.length})
              </h2>
            </div>
            <div className="p-4 space-y-3">
              {pendingInvitations.map(inv => (
                <div key={inv.id} className="border border-orange-200 rounded-lg p-4 bg-orange-50 dark:bg-orange-950/40">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-text-primary">{inv.nom}</h4>
                      <p className="text-sm text-text-secondary">
                        📅 {formatDate(inv.date_debut)} - 📍 {inv.lieu}
                      </p>
                      {inv.forfait_extra && (
                        <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                          💰 {parseFloat(inv.forfait_extra).toFixed(2)} €
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => respondToInvitation(inv.id, 'accepted')}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold"
                      >
                        Accepter
                      </button>
                      <button
                        onClick={() => respondToInvitation(inv.id, 'declined')}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold"
                      >
                        Décliner
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Planning de la semaine */}
        {mySchedule.length > 0 && (
          <div className="bg-surface rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-text-primary mb-3 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-cyan-500" />
              Planning de la semaine
            </h2>
            <div className="space-y-2">
              {mySchedule.map((sched, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-elevated-1 rounded-lg">
                  <div>
                    <p className="font-semibold text-text-primary">{sched.title || sched.shift_type}</p>
                    <p className="text-sm text-text-secondary">
                      {new Date(sched.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-blue-600">{sched.start_time} - {sched.end_time}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      sched.entry_type === 'evenement' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                    }`}>
                      {sched.entry_type === 'evenement' ? 'Événement' : sched.shift_type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setCurrentView('evenements')}
              className="mt-4 w-full text-center text-blue-500 hover:text-blue-700 dark:text-blue-300 text-sm font-semibold"
            >
              Voir tout le planning →
            </button>
          </div>
        )}

        {/* Prochain événement */}
        {loadingEvents ? (
          <div className="bg-surface rounded-xl shadow-lg p-6 text-center">
            <p className="text-text-muted">Chargement des événements...</p>
          </div>
        ) : nextEvent ? (
          <div className="bg-surface rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
              <h2 className="text-xl font-bold text-white flex items-center">
                <Calendar className="w-6 h-6 mr-2" />
                Prochain Événement
              </h2>
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-bold text-text-primary mb-4">{nextEvent.nom}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-text-secondary">Date</p>
                    <p className="font-semibold text-text-primary">{formatDate(nextEvent.date_debut)}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-purple-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-text-secondary">Horaires</p>
                    <p className="font-semibold text-text-primary">
                      {formatTime(nextEvent.date_debut)} - {formatTime(nextEvent.date_fin)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-text-secondary">Lieu</p>
                    <p className="font-semibold text-text-primary">{nextEvent.lieu}</p>
                  </div>
                </div>
                
                {nextEvent.forfait_extra && (
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 text-green-500 mt-1 flex-shrink-0">💰</div>
                    <div>
                      <p className="text-sm text-text-secondary">Forfait</p>
                      <p className="font-semibold text-green-600 dark:text-green-400 text-lg">
                        {parseFloat(nextEvent.forfait_extra).toFixed(2)} €
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {nextEvent.description && (
                <div className="mt-4 p-4 bg-elevated-1 rounded-lg">
                  <p className="text-sm text-text-secondary mb-1">Description</p>
                  <p className="text-text-secondary">{nextEvent.description}</p>
                </div>
              )}
              
              <button
                onClick={() => setCurrentView('evenements')}
                className="mt-6 w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition shadow-md"
              >
                Voir tous mes événements
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-surface rounded-xl shadow-lg p-8 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-text-primary mb-2">Aucun événement à venir</h3>
            <p className="text-text-secondary">Vous n'avez pas d'événement programmé pour le moment.</p>
          </div>
        )}

        {/* Historique des invitations */}
        {myInvitations.length > 0 && (
          <div className="bg-surface rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-text-primary mb-3">
              Historique des invitations
            </h2>
            <div className="space-y-2">
              {myInvitations.filter(inv => inv.status !== 'invited').slice(0, 10).map(inv => (
                <div key={inv.id} className="flex items-center justify-between p-3 bg-elevated-1 rounded-lg">
                  <div>
                    <p className="font-semibold text-text-primary">{inv.nom}</p>
                    <p className="text-xs text-text-muted">{formatDate(inv.date_debut)}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded font-semibold ${
                    inv.status === 'accepted' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                  }`}>
                    {inv.status === 'accepted' ? 'Acceptée' : 'Déclinée'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Dashboard spécifique pour le Patron - Vue synthétique rapide
  const PatronDashboardView = () => {
    return <DashboardPatron authToken={authToken} employees={employees} authUser={authUser} onNavigate={setCurrentView} />;
  };

  // Ancien Dashboard Patron (gardé pour référence, non utilisé)
  const PatronDashboardViewLegacy = () => {
    const [liveData, setLiveData] = useState({
      presences: [],
      projets: [],
      taches: [],
      evenements: [],
      loading: true
    });

    useEffect(() => {
      const fetchLiveData = async () => {
        try {
          // Récupérer tous les pointages du jour
          const pointagesRes = await fetch(`${API_URL}/pointages`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          const allPointages = await pointagesRes.json();
          
          const today = new Date().toLocaleDateString('fr-FR');
          const pointagesToday = allPointages.filter(p => {
            const pDate = new Date(p.date).toLocaleDateString('fr-FR');
            return pDate === today;
          });

          // Enrichir avec les infos employés
          const presencesEnrichies = pointagesToday.map(p => {
            const emp = employees.find(e => e.id === p.user_id);
            return {
              ...p,
              employee: emp,
              duree: p.heure_depart ? calculateDuration(p.heure_arrivee, p.heure_depart) : calculateDuration(p.heure_arrivee, new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }))
            };
          }).filter(p => p.employee);

          // Récupérer projets avec membres
          const projetsRes = await fetch(`${API_URL}/projects`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          const projetsData = await projetsRes.json();

          // Récupérer tâches
          const tachesRes = await fetch(`${API_URL}/tasks`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          const tachesData = await tachesRes.json();

          // Récupérer événements
          const evenementsRes = await fetch(`${API_URL}/evenements`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          const evenementsData = await evenementsRes.json();

          setLiveData({
            presences: presencesEnrichies,
            projets: projetsData,
            taches: tachesData,
            evenements: evenementsData,
            loading: false
          });
        } catch (error) {
          console.error('Erreur chargement données live:', error);
          setLiveData(prev => ({ ...prev, loading: false }));
        }
      };

      if (authToken && employees.length > 0) {
        fetchLiveData();
        // Actualiser toutes les 30 secondes
        const interval = setInterval(fetchLiveData, 30000);
        return () => clearInterval(interval);
      }
    }, [authToken, employees]);

    const calculateDuration = (start, end) => {
      if (!start || !end) return '0h00';
      const [startH, startM] = start.split(':').map(Number);
      const [endH, endM] = end.split(':').map(Number);
      let minutes = (endH * 60 + endM) - (startH * 60 + startM);
      if (minutes < 0) minutes += 24 * 60;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h${mins.toString().padStart(2, '0')}`;
    };

    const getEmployeeTasks = (employeeId) => {
      return liveData.taches.filter(t => 
        t.assigned_to === employeeId && t.status !== 'Terminé'
      );
    };

    const getEmployeeProjects = (employeeId) => {
      return liveData.projets.filter(p => 
        p.members && p.members.some(m => m.employee_id === employeeId)
      );
    };

    const getEmployeeEvents = (employeeId) => {
      const now = new Date();
      return liveData.evenements.filter(e => {
        const eventDate = new Date(e.date_debut);
        return eventDate >= now && 
               e.participants && 
               e.participants.some(p => p.employee_id === employeeId);
      });
    };

    if (liveData.loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-500 mx-auto"></div>
            <p className="mt-4 text-text-secondary">Chargement du tableau de bord...</p>
          </div>
        </div>
      );
    }

    const presentsCount = liveData.presences.filter(p => p.statut === 'en_cours').length;
    const absentsCount = employees.length - presentsCount;

    return (
      <div className="space-y-6">
        {/* En-tête avec statistiques rapides */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <h1 className="text-3xl font-bold mb-2">👑 Vue d'ensemble Patron</h1>
          <p className="text-purple-100">Supervision en temps réel de toute l'entreprise</p>
        </div>

        {/* Statistiques clés */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Présents maintenant</p>
                <p className="text-4xl font-bold mt-2">{presentsCount}/{employees.length}</p>
              </div>
              <Users className="w-12 h-12 text-green-200" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Projets actifs</p>
                <p className="text-4xl font-bold mt-2">{liveData.projets.filter(p => p.status === 'En cours').length}</p>
              </div>
              <Folder className="w-12 h-12 text-blue-200" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Tâches en cours</p>
                <p className="text-4xl font-bold mt-2">{liveData.taches.filter(t => t.status === 'En cours').length}</p>
              </div>
              <CheckSquare className="w-12 h-12 text-orange-200" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Événements à venir</p>
                <p className="text-4xl font-bold mt-2">{liveData.evenements.filter(e => new Date(e.date_debut) >= new Date()).length}</p>
              </div>
              <Calendar className="w-12 h-12 text-red-200" />
            </div>
          </div>
        </div>

        {/* Vue détaillée par employé */}
        <div className="bg-surface rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-text-primary flex items-center">
              <Users className="w-8 h-8 mr-3 text-purple-600" />
              Qui fait quoi en ce moment
            </h2>
            <button
              onClick={() => window.location.reload()}
              className="bg-purple-100 dark:bg-purple-900/40 hover:bg-purple-200 dark:bg-purple-800/40 text-purple-700 dark:text-purple-300 px-4 py-2 rounded-lg font-semibold transition flex items-center space-x-2"
            >
              <span>🔄</span>
              <span>Actualiser</span>
            </button>
          </div>

          <div className="space-y-4">
            {employees.map(emp => {
              const presence = liveData.presences.find(p => p.user_id === emp.id);
              const isPresent = presence && presence.statut === 'en_cours';
              const tasks = getEmployeeTasks(emp.id);
              const projects = getEmployeeProjects(emp.id);
              const events = getEmployeeEvents(emp.id);

              return (
                <div
                  key={emp.id}
                  className={`border-2 rounded-xl p-6 transition ${
                    isPresent
                      ? 'border-green-300 bg-green-50 dark:bg-green-950/40'
                      : 'border-border-subtle bg-elevated-1 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    {/* Infos employé */}
                    <div className="flex items-center space-x-4 flex-1">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white ${
                        isPresent ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gray-400'
                      }`}>
                        {emp.avatar || `${emp.first_name[0]}${emp.last_name[0]}`}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-xl font-bold text-text-primary">
                            {emp.first_name} {emp.last_name}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            emp.role_name === 'Patron' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200' :
                            emp.role_name === 'Super Manager' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200' :
                            emp.role_name === 'Manager' ? 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200' :
                            emp.role_name === 'Extra' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200' :
                            'bg-elevated-1 text-text-primary'
                          }`}>
                            {emp.role_name}
                          </span>
                          <span className="text-sm text-text-secondary">
                            {emp.service_name || 'Intervenant externe'}
                          </span>
                        </div>

                        {/* Statut présence */}
                        <div className="flex items-center space-x-4 mb-3">
                          {isPresent ? (
                            <>
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-green-700 dark:text-green-300 font-semibold">Présent</span>
                              </div>
                              <span className="text-text-secondary">
                                Depuis {presence.heure_arrivee}
                              </span>
                              <span className="text-text-muted text-sm">
                                ({presence.duree})
                              </span>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                                <span className="text-text-muted font-semibold">Absent</span>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Activités en cours */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          {/* Projets */}
                          <div className="bg-surface rounded-lg p-3 border border-blue-200">
                            <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2 flex items-center">
                              <Folder className="w-4 h-4 mr-1" />
                              Projets ({projects.length})
                            </h4>
                            {projects.length > 0 ? (
                              <ul className="space-y-2">
                                {projects.slice(0, 2).map(p => {
                                  const totalTasks = p.tasks?.length || 0;
                                  const doneTasks = p.tasks?.filter(t => t.status === 'Terminé').length || 0;
                                  const progress = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
                                  return (
                                    <li key={p.id}>
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="text-sm text-text-secondary truncate max-w-[70%]">• {p.name}</span>
                                        <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">{progress}%</span>
                                      </div>
                                      <div className="w-full bg-elevated-1 rounded-full h-1.5">
                                        <div
                                          className="bg-blue-500 h-1.5 rounded-full transition-all"
                                          style={{ width: `${progress}%` }}
                                        />
                                      </div>
                                      {totalTasks > 0 && (
                                        <p className="text-xs text-text-muted mt-0.5">{doneTasks}/{totalTasks} tâches</p>
                                      )}
                                    </li>
                                  );
                                })}
                                {projects.length > 2 && (
                                  <li className="text-xs text-text-muted">+{projects.length - 2} autre(s)</li>
                                )}
                              </ul>
                            ) : (
                              <p className="text-xs text-text-muted">Aucun projet</p>
                            )}
                          </div>

                          {/* Tâches */}
                          <div className="bg-surface rounded-lg p-3 border border-orange-200">
                            <h4 className="text-sm font-semibold text-orange-700 dark:text-orange-300 mb-2 flex items-center">
                              <CheckSquare className="w-4 h-4 mr-1" />
                              Tâches ({tasks.length})
                            </h4>
                            {tasks.length > 0 ? (
                              <ul className="space-y-2">
                                {tasks.slice(0, 2).map(t => {
                                  const statusColor =
                                    t.status === 'Terminé' ? 'bg-green-500' :
                                    t.status === 'En cours' ? 'bg-orange-400' :
                                    'bg-gray-300';
                                  const statusPct =
                                    t.status === 'Terminé' ? 100 :
                                    t.status === 'En cours' ? 50 :
                                    0;
                                  return (
                                    <li key={t.id}>
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="text-sm text-text-secondary truncate max-w-[65%]">• {t.title}</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                          t.status === 'Terminé' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                                          t.status === 'En cours' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' :
                                          'bg-elevated-1 text-text-muted'
                                        }`}>{t.status}</span>
                                      </div>
                                      <div className="w-full bg-elevated-1 rounded-full h-1.5">
                                        <div
                                          className={`${statusColor} h-1.5 rounded-full transition-all`}
                                          style={{ width: `${statusPct}%` }}
                                        />
                                      </div>
                                    </li>
                                  );
                                })}
                                {tasks.length > 2 && (
                                  <li className="text-xs text-text-muted">+{tasks.length - 2} autre(s)</li>
                                )}
                              </ul>
                            ) : (
                              <p className="text-xs text-text-muted">Aucune tâche</p>
                            )}
                          </div>

                          {/* Événements */}
                          <div className="bg-surface rounded-lg p-3 border border-red-200">
                            <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 mb-2 flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              Événements ({events.length})
                            </h4>
                            {events.length > 0 ? (
                              <ul className="space-y-2">
                                {events.slice(0, 2).map(e => (
                                  <li key={e.id}>
                                    <p className="text-sm text-text-secondary truncate">• {e.nom}</p>
                                    <p className="text-xs text-red-500 mt-0.5">
                                      📅 {new Date(e.date_debut).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      {e.date_fin && e.date_fin !== e.date_debut &&
                                        ` → ${new Date(e.date_fin).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`
                                      }
                                    </p>
                                  </li>
                                ))}
                                {events.length > 2 && (
                                  <li className="text-xs text-text-muted">+{events.length - 2} autre(s)</li>
                                )}
                              </ul>
                            ) : (
                              <p className="text-xs text-text-muted">Aucun événement</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const DashboardView = () => {
    // Si l'utilisateur est Extra, afficher le dashboard spécialisé
    if (authUser && authUser.role_name === 'Extra') {
      return <ExtraDashboardView />;
    }

    // Si l'utilisateur est Patron ou Super Manager, afficher le dashboard Patron
    if (authUser && ['Patron', 'Super Manager'].includes(authUser.role_name)) {
      return <PatronDashboardView />;
    }

    // Si l'utilisateur est Employé ou Manager, afficher le dashboard Employé
    if (authUser && ['Employé', 'Manager'].includes(authUser.role_name)) {
      return (
        <DashboardEmployee
          authUser={authUser}
          authToken={authToken}
          employees={employees}
          pointages={pointages}
          tasks={tasks}
          schedules={schedules}
          onNavigate={setCurrentView}
          onRefresh={loadAllData}
        />
      );
    }

    // Dashboard standard fallback pour les rôles inconnus
    return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-semibold">Erreur de connexion</p>
            <p className="text-red-600 text-sm">{error}</p>
            <p className="text-red-600 text-sm mt-1">Vérifiez que le serveur API est démarré avec: <code className="bg-red-100 dark:bg-red-900/40 px-2 py-1 rounded">npm start</code></p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Employés Présents</p>
              <p className="text-3xl font-bold mt-2">{stats.present}/{stats.totalEmployees}</p>
            </div>
            <Users className="w-12 h-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Tâches En Cours</p>
              <p className="text-3xl font-bold mt-2">{stats.tasksInProgress}</p>
            </div>
            <CheckSquare className="w-12 h-12 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Tâches À Faire</p>
              <p className="text-3xl font-bold mt-2">{stats.tasksTodo}</p>
            </div>
            <Calendar className="w-12 h-12 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Total Services</p>
              <p className="text-3xl font-bold mt-2">{services.length}</p>
            </div>
            <Briefcase className="w-12 h-12 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Pointage Rapide (pour tous) */}
      <div className="bg-surface rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-text-primary mb-4">Pointage Rapide</h2>
        {(() => {
          // Trouver le pointage actif de l'utilisateur aujourd'hui
          const userPointageActif = pointagesAujourdhui.find(p => p.user_id === authUser?.id);
          const estEnCours = userPointageActif && userPointageActif.statut === 'en_cours';
          const estTermine = userPointageActif && userPointageActif.statut === 'termine';

          if (estTermine) {
            return (
              <div className="text-center py-4">
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-lg font-semibold">
                  <CheckSquare className="w-5 h-5" />
                  <span>Pointage terminé pour aujourd'hui</span>
                </div>
                <p className="text-sm text-text-secondary mt-2">
                  Arrivée: {userPointageActif.heure_arrivee} • Départ: {userPointageActif.heure_depart}
                </p>
              </div>
            );
          }

          if (estEnCours) {
            return (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-lg font-semibold">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <span>En cours depuis {userPointageActif.heure_arrivee}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleCheckOut(authUser.id)}
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 transition shadow-md font-semibold"
                  >
                    <Clock className="w-5 h-5" />
                    <span>Départ</span>
                  </button>
                  <button
                    onClick={() => setCurrentView('pointeuse')}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 transition shadow-md font-semibold"
                  >
                    <Clock className="w-5 h-5" />
                    <span>Pause</span>
                  </button>
                </div>
              </div>
            );
          }

          return (
            <button
              onClick={() => handleCheckIn(authUser.id)}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-4 rounded-lg flex items-center justify-center space-x-3 transition shadow-md font-semibold text-lg"
            >
              <Clock className="w-6 h-6" />
              <span>Pointer l'Arrivée</span>
            </button>
          );
        })()}
      </div>

      {/* Congés */}
      <div className="bg-surface rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-text-primary">Congés</h2>
            <p className="text-sm text-text-secondary mt-1">Gérer vos demandes de congés</p>
          </div>
          <button
            onClick={() => {
              setCurrentView('rh');
              setRhSubView('conges');
            }}
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition shadow-md font-semibold"
          >
            <Calendar className="w-5 h-5" />
            <span>Demander un congé</span>
          </button>
        </div>
      </div>

      {/* Présences du Jour (Patron, Super Manager, Manager uniquement) */}
      {authUser && ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name) && (
        <div className="bg-surface rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-text-primary">Présences du Jour</h2>
            <button
              onClick={loadAllData}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:text-blue-300 text-sm font-semibold flex items-center space-x-1"
            >
              <span>🔄 Actualiser</span>
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-border-subtle">
                  <th className="text-left py-3 px-4 text-text-secondary font-semibold">Employé</th>
                  <th className="text-left py-3 px-4 text-text-secondary font-semibold">Rôle</th>
                  <th className="text-left py-3 px-4 text-text-secondary font-semibold">Service</th>
                  <th className="text-left py-3 px-4 text-text-secondary font-semibold">Statut</th>
                  <th className="text-left py-3 px-4 text-text-secondary font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees
                  .filter(emp => {
                    // Patron et Super Manager voient tous les employés
                    if (['Patron', 'Super Manager'].includes(authUser.role_name)) {
                      return true;
                    }
                    // Manager voit uniquement les employés de son service
                    if (authUser.role_name === 'Manager') {
                      return emp.service_name === authUser.service_name;
                    }
                    return false;
                  })
                  .map(emp => {
                // Trouver le pointage actif de l'employé aujourd'hui
                const pointageActif = pointagesAujourdhui.find(p => p.user_id === emp.id);
                const estPresent = !!pointageActif;

                return (
                  <tr key={emp.id} className="border-b border-border-subtle hover:bg-elevated-1">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold mr-3">
                          {emp.avatar}
                        </div>
                        <div>
                          <p className="font-semibold text-text-primary">{emp.first_name} {emp.last_name}</p>
                          <p className="text-sm text-text-muted">{emp.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        emp.role_name === 'Patron' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' :
                        emp.role_name === 'Manager' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                        'bg-elevated-1 text-text-secondary'
                      }`}>
                        {emp.role_name}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-text-secondary">{emp.service_name}</td>
                    <td className="py-3 px-4">
                      <div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          estPresent
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                        }`}>
                          {estPresent ? 'Présent' : 'Absent'}
                        </span>
                        {estPresent && pointageActif.heure_arrivee && (
                          <p className="text-xs text-text-muted mt-1">
                            Depuis {pointageActif.heure_arrivee}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => {
                          setCurrentUser(emp);
                          setCurrentView('pointeuse');
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition"
                      >
                        <Clock className="w-4 h-4" />
                        <span>Pointeuse</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
    ); // Fin du dashboard standard
  }; // Fin du DashboardView

  const PointeuseView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-text-primary">Pointeuse</h2>
      </div>

      {/* Sélection de l'utilisateur (Patron, Super Manager, Manager uniquement) */}
      {!currentUser && authUser && ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name) && (() => {
        const ROLE_COLORS_PTG = {
          'Patron':        { avatar: 'from-red-400 to-red-600',     badge: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' },
          'Super Manager': { avatar: 'from-violet-400 to-violet-600', badge: 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300' },
          'Manager':       { avatar: 'from-blue-400 to-blue-600',   badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
          'Employé':       { avatar: 'from-green-400 to-green-600', badge: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' },
          'Extra':         { avatar: 'from-orange-400 to-orange-600', badge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' },
        };

        // Base : filtrage par rôle
        let base = employees.filter(emp => {
          if (['Patron', 'Super Manager'].includes(authUser.role_name)) return true;
          if (authUser.role_name === 'Manager') return emp.service_name === authUser.service_name;
          return true;
        });

        // Filtre Extras
        if (ptgExtrasFilter === 'sans') base = base.filter(e => e.role_name !== 'Extra');
        else if (ptgExtrasFilter === 'uniquement') base = base.filter(e => e.role_name === 'Extra');

        // Filtre recherche
        if (ptgSearch) {
          const s = ptgSearch.toLowerCase();
          base = base.filter(e =>
            `${e.first_name} ${e.last_name}`.toLowerCase().includes(s) ||
            (e.role_name || '').toLowerCase().includes(s) ||
            (e.service_name || '').toLowerCase().includes(s)
          );
        }

        // Session user en premier
        const authId = authUser.employee_id || authUser.id;
        const selfEmp = base.find(e => e.id === authId);
        if (selfEmp) base = [selfEmp, ...base.filter(e => e.id !== authId)];

        return (
          <div className="bg-surface rounded-2xl shadow-md border border-border-subtle p-5 space-y-4">
            {/* Header + filtres */}
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-base font-bold text-text-primary mr-2">Sélectionnez votre profil</h3>
              {/* Recherche */}
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={ptgSearch}
                  onChange={e => setPtgSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-border-subtle rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              {/* Toggle Extras */}
              <div className="flex rounded-lg border border-border-subtle overflow-hidden text-xs font-semibold">
                {[
                  { key: 'sans',      label: 'Sans extras' },
                  { key: 'tous',      label: 'Tous' },
                  { key: 'uniquement',label: 'Extras seult.' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setPtgExtrasFilter(opt.key)}
                    className={`px-3 py-2 transition ${
                      ptgExtrasFilter === opt.key
                        ? 'bg-blue-600 text-white'
                        : 'bg-surface text-text-secondary hover:bg-elevated-1'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Grille */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {base.map(emp => {
                const rc = ROLE_COLORS_PTG[emp.role_name] || ROLE_COLORS_PTG['Employé'];
                const isSelf = emp.id === authId;
                return (
                  <button
                    key={emp.id}
                    onClick={() => setCurrentUser(emp)}
                    className={`relative p-3 rounded-xl border-2 text-left transition hover:shadow-md ${
                      isSelf
                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/40'
                        : 'border-border-subtle bg-surface hover:border-blue-300 dark:border-blue-700'
                    }`}
                  >
                    {isSelf && (
                      <span className="absolute top-2 right-2 text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded-full font-bold">Moi</span>
                    )}
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${rc.avatar} flex items-center justify-center text-white font-bold text-sm mb-2 shadow-sm`}>
                      {emp.avatar}
                    </div>
                    <p className="font-semibold text-text-primary text-sm leading-tight truncate">{emp.first_name} {emp.last_name}</p>
                    <span className={`inline-block mt-1 text-[11px] px-1.5 py-0.5 rounded-full font-medium ${rc.badge}`}>
                      {emp.role_name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Afficher la pointeuse si un utilisateur est sélectionné */}
      {currentUser && (
        <>
          <div className="bg-surface rounded-xl shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold">
                  {currentUser.avatar}
                </div>
                <div>
                  <p className="font-semibold text-text-primary">{currentUser.first_name} {currentUser.last_name}</p>
                  <p className="text-sm text-text-muted">{currentUser.role_name}{currentUser.role_name !== 'Extra' && currentUser.service_name ? ` - ${currentUser.service_name}` : ''}</p>
                </div>
              </div>
              {/* Bouton Changer d'utilisateur (Patron, Super Manager, Manager uniquement) */}
              {authUser && ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name) && (
                <button
                  onClick={() => {
                    setCurrentUser(null);
                    setSelectedEmployeeId(null);
                  }}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-300 text-sm font-semibold"
                >
                  Changer d'utilisateur
                </button>
              )}
            </div>
          </div>

          <PointeuseAvancee
            currentUser={currentUser}
            pointages={pointages}
            onUpdate={loadAllData}
            employees={employees}
            authToken={authToken}
            authUser={authUser}
            selectedEmployeeId={selectedEmployeeId}
            setSelectedEmployeeId={setSelectedEmployeeId}
          />
        </>
      )}
    </div>
  );

  // ==================== RENDER PRINCIPAL ====================

  // Afficher Login/Register/ForgotPassword/ResetPassword si pas authentifié
  if (!isAuthenticated) {
    // Afficher le formulaire de réinitialisation si un token est présent
    if (resetToken && resetEmail) {
      return (
        <ResetPassword
          token={resetToken}
          email={resetEmail}
          onSwitchToLogin={() => {
            setResetToken(null);
            setResetEmail(null);
            setShowRegister(false);
            setShowForgotPassword(false);
          }}
        />
      );
    }

    // Afficher le formulaire de mot de passe oublié
    if (showForgotPassword) {
      return (
        <ForgotPassword
          onSwitchToLogin={() => {
            setShowForgotPassword(false);
            setShowRegister(false);
          }}
        />
      );
    }

    if (showRegister) {
      return (
        <Register
          onRegister={handleLogin}
          onSwitchToLogin={() => setShowRegister(false)}
          onSwitchToForgotPassword={() => {
            setShowRegister(false);
            setShowForgotPassword(true);
          }}
        />
      );
    }
    return (
      <Login
        onLogin={handleLogin}
        onSwitchToRegister={() => setShowRegister(true)}
        onSwitchToForgotPassword={() => setShowForgotPassword(true)}
      />
    );
  }

  if (loading && employees.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-950 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto animate-pulse space-y-4">
          <div className="h-14 bg-surface rounded-xl shadow" />
          <div className="h-10 bg-surface rounded-xl shadow w-3/4" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="h-24 bg-gray-200 rounded-2xl" />
            <div className="h-24 bg-gray-200 rounded-2xl" />
            <div className="h-24 bg-gray-200 rounded-2xl" />
            <div className="h-24 bg-gray-200 rounded-2xl" />
          </div>
          <div className="h-48 bg-gray-200 rounded-2xl" />
          <div className="h-48 bg-gray-200 rounded-2xl" />
        </div>
      </div>
    );
  }

  // Données des onglets de navigation (selon le rôle)
  const navTabs = authUser?.role_name === 'Extra' ? [
    { id: 'dashboard',  label: 'Tableau de Bord', shortLabel: 'Accueil',  Icon: Home,          gradient: 'from-blue-500 to-blue-600' },
    { id: 'evenements', label: 'Événements',      shortLabel: 'Événem.',  Icon: PartyPopper,   gradient: 'from-orange-500 to-red-500' },
    { id: 'taches',     label: 'Tâches',          shortLabel: 'Tâches',   Icon: CheckSquare,   gradient: 'from-emerald-500 to-emerald-600' },
    { id: 'messagerie', label: 'Messagerie',      shortLabel: 'Messages', Icon: MessageSquare, gradient: 'from-pink-500 to-pink-600' },
    { id: 'pointeuse',  label: 'Pointeuse',       shortLabel: 'Pointer',  Icon: Clock,         gradient: 'from-indigo-500 to-indigo-600' },
    { id: 'documents_rh', label: 'Documents RH',  shortLabel: 'Doc. RH',  Icon: FileText,      gradient: 'from-slate-600 to-slate-700' },
  ] : [
    { id: 'dashboard',  label: 'Tableau de Bord', shortLabel: 'Accueil',  Icon: Home,          gradient: 'from-blue-500 to-blue-600' },
    { id: 'pointeuse',  label: 'Pointeuse',       shortLabel: 'Pointer',  Icon: Clock,         gradient: 'from-indigo-500 to-indigo-600' },
    { id: 'planning',   label: 'Planning',        shortLabel: 'Planning', Icon: Calendar,      gradient: 'from-cyan-500 to-cyan-600' },
    { id: 'messagerie', label: 'Messagerie',      shortLabel: 'Messages', Icon: MessageSquare, gradient: 'from-pink-500 to-pink-600' },
    { id: 'evenements', label: 'Événements',      shortLabel: 'Événem.',  Icon: PartyPopper,   gradient: 'from-orange-500 to-red-500' },
    { id: 'taches',     label: 'Tâches',          shortLabel: 'Tâches',   Icon: CheckSquare,   gradient: 'from-emerald-500 to-emerald-600' },
    { id: 'projets',    label: 'Projets',         shortLabel: 'Projets',  Icon: Folder,        gradient: 'from-violet-500 to-violet-600' },
    { id: 'rh',         label: 'RH',              shortLabel: 'RH',       Icon: UserCog,       gradient: 'from-purple-500 to-purple-600' },
    { id: 'vehicules',  label: 'Véhicules',       shortLabel: 'Véhicules',Icon: Car,           gradient: 'from-teal-500 to-teal-600' },
  ];
  const activeNavTab = navTabs.find(t => t.id === currentView) || navTabs[0];

  // Bottom nav : 4 premiers onglets + bouton "Plus" (5 max pour les Extras = tous visibles)
  const bottomNavTabs = authUser?.role_name === 'Extra' ? navTabs : navTabs.slice(0, 4);
  const moreMenuTabs = authUser?.role_name === 'Extra' ? [] : navTabs.slice(4);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-950">
      {/* ============ HEADER — minimaliste, 64 px desktop / 56 px mobile ============ */}
      <nav className="bg-surface/95 backdrop-blur-sm shadow-sm border-b border-border-subtle sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between gap-3 h-14 sm:h-16">

            {/* === Bloc gauche : logo + titre === */}
            <button
              type="button"
              onClick={() => setCurrentView('dashboard')}
              className="flex items-center gap-2 sm:gap-3 min-w-0 rounded-lg hover:bg-elevated-1 transition-colors px-1.5 py-1 -ml-1.5"
              aria-label="Retour au tableau de bord"
            >
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-surface rounded-lg flex items-center justify-center border border-border-subtle flex-shrink-0">
                <img src="/android-chrome-192x192.png" alt="" className="w-6 h-6 sm:w-7 sm:h-7 rounded" />
              </div>
              <div className="text-left min-w-0">
                <p className="text-sm sm:text-base font-bold text-text-primary leading-tight truncate">
                  Staff Manager <span className="hidden sm:inline">Pro</span>
                </p>
                <p className="text-[10px] sm:text-xs text-text-muted leading-tight">Best Events</p>
              </div>
            </button>

            {/* === Bloc centre : barre de recherche (desktop ≥ md) === */}
            <button
              type="button"
              onClick={() => setShowCommandPalette(true)}
              className="hidden md:flex items-center gap-2 flex-1 max-w-md mx-4 px-3 py-2 min-h-[40px] text-sm text-text-muted bg-elevated-1 hover:bg-elevated-2 border border-border-subtle rounded-lg transition-colors"
              aria-label="Ouvrir la recherche rapide (Ctrl+K)"
              title="Recherche rapide (Ctrl+K)"
            >
              <Search className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span className="flex-1 text-left">Rechercher…</span>
              <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-surface border border-border-subtle rounded text-text-muted">
                Ctrl+K
              </kbd>
            </button>

            {/* === Bloc droite : actions + utilisateur === */}
            {authUser && (
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Recherche mobile (icône seule) */}
                <button
                  type="button"
                  onClick={() => setShowCommandPalette(true)}
                  className="md:hidden p-2 min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg hover:bg-elevated-1 transition-colors text-text-secondary"
                  aria-label="Recherche rapide"
                >
                  <Search className="w-5 h-5" aria-hidden="true" />
                </button>

                {/* Cluster d'actions séparé visuellement du profil */}
                <div className="flex items-center gap-1 sm:gap-1.5 sm:pr-2 sm:mr-1 sm:border-r sm:border-border-subtle">
                  <ThemeToggle compact />
                  <Notifications token={authToken} onNavigate={setCurrentView} />
                </div>

                {/* Avatar + menu utilisateur (contient déconnexion) */}
                <UserMenu user={authUser} onLogout={handleLogout} services={services} />
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-3 sm:py-8 pb-20 sm:pb-8">
        {/* Navigation tablette — barre scrollable horizontale (640px – 1023px) */}
        <div className="mb-4 bg-surface rounded-xl shadow-lg p-1.5 hidden sm:block lg:hidden">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide snap-x snap-mandatory">
            {navTabs.map(({ id, shortLabel, label, Icon, gradient }) => (
              <button
                key={id}
                onClick={() => setCurrentView(id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all font-semibold whitespace-nowrap text-sm snap-start active:scale-[0.97] active:opacity-90 ${
                  currentView === id
                    ? `bg-gradient-to-r ${gradient} text-white shadow-md`
                    : 'text-text-secondary hover:bg-elevated-1'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{shortLabel || label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Navigation principale — desktop uniquement (≥ 1024px) */}
        <div className="mb-8 bg-surface rounded-xl shadow-lg p-2 hidden lg:block">
          <div className="flex flex-col gap-1">
            {/* Ligne 1 : 8 premiers onglets */}
            <div className="flex gap-1">
              {navTabs.slice(0, 8).map(({ id, label, Icon, gradient }) => (
                <button
                  key={id}
                  onClick={() => setCurrentView(id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-2 py-2.5 rounded-lg transition-all font-semibold whitespace-nowrap active:scale-[0.97] ${
                    currentView === id
                      ? `bg-gradient-to-r ${gradient} text-white shadow-md`
                      : 'text-text-secondary hover:bg-elevated-1'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
            {/* Ligne 2 : onglets restants */}
            {navTabs.length > 8 && (
              <div className="flex gap-1 border-t border-border-subtle pt-1">
                {navTabs.slice(8).map(({ id, label, Icon, gradient }) => (
                  <button
                    key={id}
                    onClick={() => setCurrentView(id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold whitespace-nowrap ${
                      currentView === id
                        ? `bg-gradient-to-r ${gradient} text-white shadow-md`
                        : 'text-text-secondary hover:bg-elevated-1'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Indicateur mobile "Dernière mise à jour" + bouton rafraîchir */}
        <div className="flex items-center justify-between mb-3 sm:hidden">
          <span className="text-xs text-text-muted">
            {lastUpdate ? `Mis à jour à ${lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
          <button
            onClick={() => { setRefreshing(true); loadAllData(); }}
            disabled={refreshing}
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-text-muted hover:text-purple-600 hover:bg-purple-50 dark:bg-purple-950/40 active:scale-[0.97] transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="transition-opacity duration-200 ease-in-out">
        {currentView === 'dashboard' && <DashboardView />}
        {currentView === 'pointeuse' && <PointeuseView />}
        {currentView === 'planning' && <Planning employees={employees} pointages={pointages} authUser={authUser} />}
        {currentView === 'projets' && <ProjectWorkspace employees={employees} authToken={authToken} authUser={authUser} onNavigate={setCurrentView} />}
        {currentView === 'taches' && <Taches employees={employees} currentUser={authUser} authToken={authToken} services={services} managerServices={managerServices} vehicules={vehicules} schedules={schedules} />}
        {currentView === 'evenements' && <Evenements authUser={authUser} authToken={authToken} employees={employees} />}
        {currentView === 'messagerie' && <Messagerie employees={employees} currentUser={authUser} authToken={authToken} />}
        {currentView === 'vehicules' && <Vehicules token={authToken} user={authUser} />}
        {currentView === 'documents_rh' && <DocumentsReglementaires authUser={authUser} authToken={authToken} />}
        {/* Objectifs et KPI avec navigation inter-modules */}
        {currentView === 'objectifs' && <Objectifs token={authToken} user={authUser} employees={employees} onNavigate={setCurrentView} />}
        {currentView === 'kpi' && <GestionProjetsKPI authToken={authToken} authUser={authUser} employees={employees} onNavigate={setCurrentView} />}
        
        {/* ============ VUE RESSOURCES HUMAINES — Sidebar verticale unifiée ============ */}
        {currentView === 'rh' && (
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Sidebar verticale (drawer mobile, sticky desktop) */}
            <RHSidebar
              role={authUser.role_name}
              activeId={rhSubView}
              onSelect={setRhSubView}
              badges={{
                inscriptions_en_attente: 0,
                conges_en_attente: 0,
                demandes_ouvertes: 0,
              }}
            />

            {/* Zone de contenu RH */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Employés (Patron/SM/Manager) */}
              {rhSubView === 'employees' && ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name) && (
                <Employees
                  authUser={authUser}
                  employees={employees}
                  roles={roles}
                  services={services}
                  loading={loading}
                  showAddEmployee={showAddEmployee}
                  setShowAddEmployee={setShowAddEmployee}
                  editingEmployee={editingEmployee}
                  setEditingEmployee={setEditingEmployee}
                  employeeForm={employeeForm}
                  setEmployeeForm={setEmployeeForm}
                  handleAddEmployee={handleAddEmployee}
                  handleUpdateEmployee={handleUpdateEmployee}
                  handleDeleteEmployee={handleDeleteEmployee}
                  resetEmployeeForm={resetEmployeeForm}
                  startEdit={startEdit}
                  authToken={authToken}
                  pointages={pointages}
                  schedules={schedules}
                />
              )}

              {/* Inscriptions (Patron/SM) */}
              {rhSubView === 'inscriptions' && ['Patron', 'Super Manager'].includes(authUser.role_name) && (
                <InscriptionRequests authToken={authToken} authUser={authUser} apiCall={apiCall} />
              )}

              {/* Congés (tous sauf Extra) */}
              {rhSubView === 'conges' && (
                <div>
                  {authUser.role_name !== 'Extra' && (
                    <CongesSoldeBanner authToken={authToken} authUser={authUser} onNavigate={setRhSubView} />
                  )}
                  <Conges authUser={authUser} employees={employees} authToken={authToken} />
                </div>
              )}

              {/* Solde CP (Patron/SM/Manager) */}
              {rhSubView === 'solde_cp' && ['Patron', 'Super Manager', 'Manager'].includes(authUser.role_name) && (
                <CongesSolde authToken={authToken} authUser={authUser} employees={employees} onNavigate={setRhSubView} />
              )}

              {/* Note RH (tous sauf Extra) */}
              {rhSubView === 'note_rh' && (
                <NoteRHConges authUser={authUser} onOpenNoteService={() => setRhSubView('documents')} />
              )}

              {/* Modulation : Patron/SM voient ModulationManagement, Manager voit ModulationWidget */}
              {rhSubView === 'modulation' && ['Patron', 'Super Manager'].includes(authUser.role_name) && (
                <ModulationManagement authToken={authToken} />
              )}
              {rhSubView === 'modulation' && authUser.role_name === 'Manager' && (
                <ModulationWidget authUser={authUser} authToken={authToken} />
              )}

              {/* Documents réglementaires (tous, y compris Extra) */}
              {rhSubView === 'documents' && (
                <DocumentsReglementaires authUser={authUser} authToken={authToken} />
              )}

              {/* Demandes (tous sauf Extra) */}
              {rhSubView === 'demandes' && (
                <Demandes token={authToken} user={authUser} />
              )}

              {/* Exports (Patron/SM uniquement) */}
              {rhSubView === 'export_paie' && ['Patron', 'Super Manager'].includes(authUser.role_name) && (
                <ExportsPayroll pointages={pointages} employees={employees} authUser={authUser} />
              )}
              {rhSubView === 'export_hcr' && ['Patron', 'Super Manager'].includes(authUser.role_name) && (
                <ExportCongesHCR authToken={authToken} authUser={authUser} />
              )}
            </div>
          </div>
        )}

        </div>
      </div>

      {/* ============ COMMAND PALETTE (Ctrl+K) ============ */}
      {authUser && (
        <CommandPalette
          open={showCommandPalette}
          onClose={() => setShowCommandPalette(false)}
          onNavigate={(view) => setCurrentView(view)}
          onNavigateRH={(view, rhSub) => { setCurrentView(view); setRhSubView(rhSub); }}
          role={authUser.role_name}
          employees={employees}
        />
      )}

      {/* ============ BOTTOM NAVIGATION BAR — Mobile uniquement (< 640px) ============ */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50">
        {/* Overlay menu "Plus" */}
        {showMoreMenu && (
          <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowMoreMenu(false)} />
            <div className="relative z-50 bg-surface rounded-t-2xl shadow-2xl border-t border-border-subtle px-4 pt-3 pb-4">
              {/* Barre de drag */}
              <div className="flex justify-center mb-3">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {moreMenuTabs.map(({ id, label, shortLabel, Icon, gradient }) => (
                  <button
                    key={id}
                    onClick={() => { setCurrentView(id); setShowMoreMenu(false); }}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl min-h-[72px] transition-all active:scale-95 ${
                      currentView === id
                        ? `bg-gradient-to-r ${gradient} text-white shadow-md`
                        : 'bg-elevated-1 text-text-secondary hover:bg-elevated-1'
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-[11px] font-medium text-center leading-tight">{shortLabel || label}</span>
                  </button>
                ))}
              </div>
              {/* Info utilisateur */}
              {authUser && (
                <div className="flex items-center gap-3 pt-3 border-t border-border-subtle">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {authUser.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text-primary truncate">{authUser.first_name} {authUser.last_name}</p>
                    <p className="text-xs text-text-muted">{authUser.role_name}</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Barre de navigation fixe */}
        <div className="bg-surface border-t border-border-subtle shadow-[0_-2px_10px_rgba(0,0,0,0.08)] flex items-stretch justify-around px-1 pb-[env(safe-area-inset-bottom)]" style={{ minHeight: 56 }}>
          {bottomNavTabs.map(({ id, shortLabel, Icon, gradient }) => {
            const isActive = currentView === id;
            return (
              <button
                key={id}
                onClick={() => { setCurrentView(id); setShowMoreMenu(false); }}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 min-h-[56px] transition-all active:scale-95 ${
                  isActive ? 'text-purple-600 dark:text-purple-400' : 'text-text-muted'
                }`}
              >
                {isActive && <div className="w-1 h-1 rounded-full bg-purple-600 mb-0.5" />}
                <Icon className={`w-5 h-5 ${isActive ? 'text-purple-600 dark:text-purple-400' : ''}`} />
                <span className={`text-[10px] leading-tight ${isActive ? 'font-semibold text-purple-600 dark:text-purple-400' : 'font-medium'}`}>{shortLabel}</span>
              </button>
            );
          })}
          {/* Bouton "Plus" — uniquement si des onglets supplémentaires existent */}
          {moreMenuTabs.length > 0 && (
            <button
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 min-h-[56px] transition-all active:scale-95 ${
                showMoreMenu || moreMenuTabs.some(t => t.id === currentView) ? 'text-purple-600 dark:text-purple-400' : 'text-text-muted'
              }`}
            >
              {moreMenuTabs.some(t => t.id === currentView) && <div className="w-1 h-1 rounded-full bg-purple-600 mb-0.5" />}
              <MoreHorizontal className="w-5 h-5" />
              <span className={`text-[10px] leading-tight ${moreMenuTabs.some(t => t.id === currentView) ? 'font-semibold text-purple-600 dark:text-purple-400' : 'font-medium'}`}>Plus</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffManagerWithAPI;